/**
 * Payment Store
 * Zustand store for vendor payment data state management
 *
 * @see api-spec.yaml - Vendor Payment section
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { VendorPaymentDataResponse } from '@/types/api/paymentTerm';

/**
 * Payment store state
 */
export interface PaymentState {
  // Data - keyed by vendorId
  paymentDataByVendor: Record<number, VendorPaymentDataResponse>;

  // UI State
  isLoading: boolean;
  error: string | null;

  // Modal state
  isPaymentModalOpen: boolean;
  currentVendorId: number | null;

  // Actions - Data
  setPaymentDataForVendor: (vendorId: number, data: VendorPaymentDataResponse) => void;
  updatePaymentDataForVendor: (vendorId: number, updates: Partial<VendorPaymentDataResponse>) => void;
  clearPaymentDataForVendor: (vendorId: number) => void;

  // Actions - UI State
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Modal
  openPaymentModal: (vendorId: number) => void;
  closePaymentModal: () => void;

  // Actions - Reset
  reset: () => void;
}

/**
 * Initial state values
 */
const initialState = {
  paymentDataByVendor: {},
  isLoading: false,
  error: null,
  isPaymentModalOpen: false,
  currentVendorId: null,
};

/**
 * Payment Zustand store
 */
export const usePaymentStore = create<PaymentState>()(
  devtools(
    (set) => ({
      ...initialState,

      // Data actions
      setPaymentDataForVendor: (vendorId, data) =>
        set(
          (state) => ({
            paymentDataByVendor: {
              ...state.paymentDataByVendor,
              [vendorId]: data,
            },
          }),
          false,
          'payment/setPaymentDataForVendor'
        ),

      updatePaymentDataForVendor: (vendorId, updates) =>
        set(
          (state) => ({
            paymentDataByVendor: {
              ...state.paymentDataByVendor,
              [vendorId]: {
                ...state.paymentDataByVendor[vendorId],
                ...updates,
              },
            },
          }),
          false,
          'payment/updatePaymentDataForVendor'
        ),

      clearPaymentDataForVendor: (vendorId) =>
        set(
          (state) => {
            const { [vendorId]: _, ...rest } = state.paymentDataByVendor;
            return { paymentDataByVendor: rest };
          },
          false,
          'payment/clearPaymentDataForVendor'
        ),

      // UI state actions
      setLoading: (isLoading) =>
        set({ isLoading }, false, 'payment/setLoading'),

      setError: (error) =>
        set({ error }, false, 'payment/setError'),

      // Modal actions
      openPaymentModal: (vendorId) =>
        set(
          {
            isPaymentModalOpen: true,
            currentVendorId: vendorId,
          },
          false,
          'payment/openPaymentModal'
        ),

      closePaymentModal: () =>
        set(
          {
            isPaymentModalOpen: false,
            currentVendorId: null,
          },
          false,
          'payment/closePaymentModal'
        ),

      // Reset action
      reset: () =>
        set(initialState, false, 'payment/reset'),
    }),
    { name: 'PaymentStore' }
  )
);

/**
 * Selectors for common queries
 */

// Get payment data for a specific vendor
export const usePaymentDataForVendor = (vendorId: number) => {
  return usePaymentStore((state) => state.paymentDataByVendor[vendorId]);
};

// Check if vendor has payment data
export const useHasPaymentData = (vendorId: number) => {
  return usePaymentStore((state) => !!state.paymentDataByVendor[vendorId]);
};

// Get outstanding balance for a vendor (not available in new API spec)
export const useOutstandingBalance = (_vendorId: number) => {
  // Outstanding balance not available in new VendorPaymentDataResponse
  return 0;
};

// Get primary currency for a vendor
export const usePrimaryCurrency = (vendorId: number) => {
  return usePaymentStore(
    (state) => state.paymentDataByVendor[vendorId]?.primaryCurrency
  );
};
